import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { MessageSquare, Check, X, Clock, Globe, Tag, User, ArrowLeftRight, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusBadge = {
  pending: "bg-amber-100 text-amber-700",
  counter_offer: "bg-blue-100 text-blue-700",
  accepted: "bg-emerald-100 text-emerald-700",
  rejected: "bg-red-100 text-red-600",
};

const statusLabel = {
  pending: "Oczekuje",
  counter_offer: "Kontroferta wyslana",
  accepted: "Zaakceptowana",
  rejected: "Odrzucona",
};

export default function NegotiationsManager() {
  const [negotiations, setNegotiations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejectDialog, setRejectDialog] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [counterDialog, setCounterDialog] = useState(null);
  const [counterPrice, setCounterPrice] = useState("");
  const [counterMessage, setCounterMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/negotiations`);
      setNegotiations(res.data);
    } catch (err) {
      toast.error("Blad ladowania negocjacji");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAccept = async (id) => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/negotiations/${id}/accept`);
      toast.success("Negocjacja zaakceptowana - utworzono indywidualna cene");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Blad akceptacji");
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!rejectDialog) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/negotiations/${rejectDialog}/reject?reason=${encodeURIComponent(rejectReason)}`);
      toast.success("Negocjacja odrzucona");
      setRejectDialog(null);
      setRejectReason("");
      fetchData();
    } catch (err) {
      toast.error("Blad odrzucania");
    } finally {
      setSubmitting(false);
    }
  };

  const openCounterDialog = (neg) => {
    setCounterDialog(neg);
    // Ustaw domyslna cene kontroferty pomiedzy propozycja a aktualna
    const suggestedPrice = Math.round((neg.current_price + neg.proposed_price) / 2);
    setCounterPrice(suggestedPrice.toString());
    setCounterMessage("");
  };

  const handleCounterOffer = async () => {
    if (!counterDialog) return;
    if (!counterPrice || parseFloat(counterPrice) <= 0) {
      toast.error("Podaj cene kontroferty");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/negotiations/${counterDialog.id}/counter-offer`, {
        counter_price: parseFloat(counterPrice),
        message: counterMessage
      });
      toast.success("Kontroferta wyslana do reklamodawcy");
      setCounterDialog(null);
      setCounterPrice("");
      setCounterMessage("");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Blad wysylania kontroferty");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="text-center py-8 text-muted-foreground">Ladowanie...</div>;

  const pendingCount = negotiations.filter(n => n.status === "pending").length;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <MessageSquare className="w-5 h-5" />
          Negocjacje cen
          {pendingCount > 0 && (
            <Badge variant="destructive">{pendingCount} oczekuje</Badge>
          )}
        </h3>
      </div>

      {negotiations.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            Brak negocjacji do wyswietlenia
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {negotiations.map(neg => (
            <Card key={neg.id} className={neg.status === "accepted" || neg.status === "rejected" ? "opacity-70" : ""}>
              <CardContent className="py-4">
                <div className="flex justify-between items-start gap-4">
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className={statusBadge[neg.status] || statusBadge.pending}>
                        {statusLabel[neg.status] || neg.status}
                      </Badge>
                      <span className="text-sm text-muted-foreground">
                        {new Date(neg.created_at).toLocaleDateString("pl-PL")}
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-4 text-sm">
                      <span className="flex items-center gap-1">
                        <User className="w-4 h-4 text-muted-foreground" />
                        {neg.advertiser_name}
                      </span>
                      <span className="flex items-center gap-1">
                        <Globe className="w-4 h-4 text-muted-foreground" />
                        {neg.site_name}
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-1 text-sm">
                      <Tag className="w-4 h-4 text-muted-foreground" />
                      <span className="font-medium">{neg.offer_name}</span>
                    </div>
                    
                    <div className="flex items-center gap-4 text-sm flex-wrap">
                      <span>
                        Aktualna: <span className="font-medium">{neg.current_price} PLN</span>
                      </span>
                      <span className="text-primary font-bold">
                        Propozycja: {neg.proposed_price} PLN
                      </span>
                      <span className="text-muted-foreground">
                        (rabat: {Math.round((1 - neg.proposed_price / neg.current_price) * 100)}%)
                      </span>
                    </div>
                    
                    {/* Pokaz kontroferte jesli istnieje */}
                    {neg.counter_price && (
                      <div className="bg-blue-50 rounded-lg p-3 space-y-1">
                        <div className="flex items-center gap-2">
                          <ArrowLeftRight className="w-4 h-4 text-blue-600" />
                          <span className="text-sm font-medium text-blue-800">
                            Kontroferta: {neg.counter_price} PLN
                          </span>
                        </div>
                        {neg.counter_by_name && (
                          <p className="text-xs text-blue-600">
                            Wyslana przez: {neg.counter_by_name} ({neg.counter_by_role === "admin" ? "Admin" : "Wydawca"})
                          </p>
                        )}
                        {neg.counter_message && (
                          <p className="text-xs text-blue-700 mt-1">{neg.counter_message}</p>
                        )}
                      </div>
                    )}
                    
                    {neg.message && (
                      <div className="bg-muted/50 rounded-md p-3 text-sm">
                        <span className="text-muted-foreground">Wiadomosc reklamodawcy: </span>
                        {neg.message}
                      </div>
                    )}
                  </div>
                  
                  {neg.status === "pending" && (
                    <div className="flex flex-col gap-2">
                      <Button
                        size="sm"
                        onClick={() => handleAccept(neg.id)}
                        className="gap-1"
                        disabled={submitting}
                      >
                        <Check className="w-4 h-4" /> Akceptuj {neg.proposed_price} PLN
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openCounterDialog(neg)}
                        className="gap-1 text-blue-600 hover:text-blue-700"
                        disabled={submitting}
                      >
                        <ArrowLeftRight className="w-4 h-4" /> Kontroferta
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setRejectDialog(neg.id)}
                        className="gap-1 text-red-500 hover:text-red-700"
                        disabled={submitting}
                      >
                        <X className="w-4 h-4" /> Odrzuc
                      </Button>
                    </div>
                  )}
                  
                  {neg.status === "counter_offer" && (
                    <div className="text-right">
                      <Badge className="bg-blue-100 text-blue-700">
                        <Clock className="w-3 h-3 mr-1" /> Czeka na reklamodawce
                      </Badge>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Dialog odrzucenia */}
      <Dialog open={!!rejectDialog} onOpenChange={() => setRejectDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Odrzuc negocjacje</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Powod odrzucenia (opcjonalnie)</label>
              <Input
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Np. Cena zbyt niska"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setRejectDialog(null)}>
                Anuluj
              </Button>
              <Button variant="destructive" onClick={handleReject} disabled={submitting}>
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Odrzuc negocjacje"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog kontroferty */}
      <Dialog open={!!counterDialog} onOpenChange={() => setCounterDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowLeftRight className="w-5 h-5 text-blue-600" />
              Wyslij kontroferte
            </DialogTitle>
          </DialogHeader>
          {counterDialog && (
            <div className="space-y-4 py-4">
              <div className="bg-slate-50 rounded-lg p-3 space-y-1 text-sm">
                <p><span className="text-muted-foreground">Strona:</span> {counterDialog.site_name}</p>
                <p><span className="text-muted-foreground">Oferta:</span> {counterDialog.offer_name}</p>
                <p><span className="text-muted-foreground">Aktualna cena:</span> <strong>{counterDialog.current_price} PLN</strong></p>
                <p><span className="text-muted-foreground">Propozycja reklamodawcy:</span> <strong className="text-primary">{counterDialog.proposed_price} PLN</strong></p>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium">Twoja kontroferta (PLN)</label>
                <Input
                  type="number"
                  value={counterPrice}
                  onChange={(e) => setCounterPrice(e.target.value)}
                  placeholder="np. 150"
                  min="1"
                />
                <p className="text-xs text-muted-foreground">
                  Sugerowana cena pomiedzy: {counterDialog.proposed_price} - {counterDialog.current_price} PLN
                </p>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium">Wiadomosc (opcjonalnie)</label>
                <Textarea
                  value={counterMessage}
                  onChange={(e) => setCounterMessage(e.target.value)}
                  placeholder="Np. Proponuje kompromis..."
                  rows={2}
                />
              </div>
              
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setCounterDialog(null)}>
                  Anuluj
                </Button>
                <Button onClick={handleCounterOffer} disabled={submitting} className="bg-blue-600 hover:bg-blue-700">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Wyslij kontroferte"}
                </Button>
              </div>
              
              <p className="text-xs text-muted-foreground text-center">
                Reklamodawca otrzyma powiadomienie i bedzie mogl zaakceptowac lub odrzucic kontroferte.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
